<h2 lvrPadding [padHorizontal]="32" [padVertical]="0">{{ 'assignment.additional.edit_title' | transloco }}</h2>

<form [formGroup]="form" autocomplete="on" lvrAutoSave="additional" [entityId]="draftId()" [autoLoad]="false">
	<lvr-section>
		<lvr-section-form>
			@let startDateCtrl = form.get('startOfCare');
			<lvr-field [label]="'assignment.create_additional.startOfCare' | transloco | appendAsterisk">
				<mat-form-field lvrInput [lvrError]="hasError(startDateCtrl, 'required', 'minDateExceeded', 'maxDateExceeded')">
					<input
						matInput
						formControlName="startOfCare"
						[matDatepicker]="startPicker"
						placeholder="01.07.2025"
						[min]="minStartDate()"
						[max]="maxStartDate()"
						[errorStateMatcher]="matcher"
					/>
					@if (hasError(startDateCtrl, 'required')) {
						<mat-error>{{ 'base.form.error.required' | transloco }}</mat-error>
					} @else if (hasError(startDateCtrl, 'minDateExceeded')) {
						<mat-error>
							{{
								'assignment.create_additional.errors.min_date'
									| transloco: { min: (minStartDate() | date: dateDisplayFormat) }
							}}
						</mat-error>
					} @else if (hasError(startDateCtrl, 'maxDateExceeded')) {
						<mat-error>{{ 'assignment.create_additional.errors.max_date' | transloco }}</mat-error>
					} @else {
						<mat-hint>{{ dateFormatHint }}</mat-hint>
					}
					<mat-datepicker-toggle matIconSuffix [for]="startPicker"></mat-datepicker-toggle>
					<mat-datepicker #startPicker></mat-datepicker>
				</mat-form-field>
			</lvr-field>

			<lvr-field [label]="'base.field.type_of_care' | transloco">
				<mat-form-field lvrInput [attr.aria-label]="'base.field.type_of_care' | transloco">
					<input matInput [value]="assignment()?.typeOfCare?.remark ?? '-'" disabled />
				</mat-form-field>
			</lvr-field>

			@let handicapCtrl = form.get('careRelatedHandicapId');
			<lvr-field
				[label]="
					isDisabilityMandatory()
						? ('base.field.type_of_disability_relevant_to_care' | transloco | appendAsterisk)
						: ('base.field.type_of_disability_relevant_to_care' | transloco)
				"
			>
				<mat-form-field lvrInput [attr.aria-label]="'base.field.select_please' | transloco">
					<mat-select
						#sel="matSelect"
						formControlName="careRelatedHandicapId"
						[placeholder]="'base.field.type_of_disability_relevant_to_care' | transloco"
						aria-required="true"
					>
						<mat-select-trigger>
							{{ getDisabilityKindName(sel.value) }}
							<span class="text-sm differentiation">{{ getDisabilityKindDifferentiation(sel.value) }}</span>
						</mat-select-trigger>

						@for (option of disabilityKinds(); track option.id) {
							<mat-option class="option" [value]="option.id">
								<span class="remark">{{ option.disabilityType?.remark ?? '' }}</span>
								<span class="text-sm">{{ option.categoryDiagnosis?.description ?? '-' }}</span>
							</mat-option>
						}
					</mat-select>
					@if (hasError(handicapCtrl, 'required')) {
						<mat-error>{{ 'base.form.error.required' | transloco }}</mat-error>
					}
				</mat-form-field>
			</lvr-field>
			@if (isPhaseP3()) {
				<!-- Datum Ende der Betreuung -->
				@let cancelCtrl = form.get('cancellationDate');
				<lvr-field [label]="'assignment.edit.cancellation_date' | transloco | appendAsterisk">
					<mat-form-field
						lvrInput
						[lvrError]="hasError(cancelCtrl, 'minDateExceeded', 'maxDateExceeded', 'matDatepickerParse', 'required')"
					>
						<input
							matInput
							[matDatepicker]="cancelPicker"
							formControlName="cancellationDate"
							placeholder="09.10.2025"
							[max]="maxCancellationDate()"
							[min]="minCancellationDate()"
							[errorStateMatcher]="matcher"
						/>
						@if (hasError(cancelCtrl, 'minDateExceeded')) {
							<mat-error>{{ 'assignment.phase.date_min' | transloco }}</mat-error>
						} @else if (hasError(cancelCtrl, 'maxDateExceeded')) {
							<mat-error>{{ 'base.form.error.maxDateExceeded' | transloco }}</mat-error>
						} @else if (hasError(cancelCtrl, 'matDatepickerParse')) {
							<mat-error>{{ 'base.form.error.date_format_invalid' | transloco: { format: dateFormatHint } }}</mat-error>
						} @else if (hasError(cancelCtrl, 'required')) {
							<mat-error>{{ 'base.form.error.required' | transloco }}</mat-error>
						}
						<mat-datepicker-toggle matIconSuffix [for]="cancelPicker"></mat-datepicker-toggle>
						<mat-datepicker #cancelPicker></mat-datepicker>
					</mat-form-field>
				</lvr-field>
			}

			@let documentsCtrl = form.get('documents');
			<lvr-field [label]="'base.field.documents' | transloco">
				<lvr-document-upload
					[maxFileCount]="maxFileCount"
					[hasError]="hasFileError(documentsCtrl, 'requiredFile', 'maxFiles', 'maxFileSize', 'allowedFileTypes')"
					[multiple]="true"
					[disabled]="!canManageAssignmentDocuments()"
					formControlName="documents"
					[showIcon]="true"
					[ownerId]="client().uuid"
					[maxFileSize]="maxFileSize"
					[subjectType]="subjectType"
				>
				</lvr-document-upload>
			</lvr-field>

			@if (isPhaseP3()) {
				@let impedimentReportDueDateCtrl = form.get('impedimentReportDueDate');
				<lvr-field [label]="'assignment.edit.impediment_report_due_date' | transloco">
					<mat-form-field
						lvrInput
						[lvrError]="
							hasError(impedimentReportDueDateCtrl, 'minDateExceeded', 'maxDateExceeded', 'matDatepickerParse')
						"
					>
						<input
							matInput
							[matDatepicker]="picker"
							formControlName="impedimentReportDueDate"
							placeholder="09.10.2025"
							[max]="isImpedimentReportDueDateDirty() ? maxImpedimentReportDueDate() : null"
							[min]="isImpedimentReportDueDateDirty() ? minImpedimentReportDueDate() : null"
							[errorStateMatcher]="matcher"
						/>
						@if (hasError(impedimentReportDueDateCtrl, 'minDateExceeded')) {
							<mat-error>{{ 'base.form.error.minDateExceeded' | transloco }}</mat-error>
						} @else if (hasError(impedimentReportDueDateCtrl, 'matDatepickerParse')) {
							<mat-error>{{ 'base.form.error.date_format_invalid' | transloco: { format: dateFormatHint } }}</mat-error>
						} @else if (hasError(impedimentReportDueDateCtrl, 'maxDateExceeded')) {
							<mat-error>{{ 'base.form.error.maxDateExceeded' | transloco }}</mat-error>
						}
						<mat-datepicker-toggle matIconSuffix [for]="picker"></mat-datepicker-toggle>
						<mat-datepicker #picker></mat-datepicker>
					</mat-form-field>
				</lvr-field>
			}
		</lvr-section-form>
	</lvr-section>

	<div class="assignment-edit-actions-row">
		<p lvrPadding [padHorizontal]="0" [padVertical]="24">{{ 'base.form.required' | transloco }}</p>
		<lvr-default-actions
			[disableSave]="form.invalid || !canEditAssignmentsPermission()"
			(save)="save()"
			(cancelSave)="cancel()"
			[showLoading]="isLoading()"
		>
			<lvr-restore-actions entityType="additional" [entityId]="draftId()"></lvr-restore-actions>
		</lvr-default-actions>
	</div>
</form>
